import '../../css/signup/signup.less';

import { useIsMounted } from '@maternity/mun-cantrips';
import env from '@maternity/mun-env';
import { Form, useForm } from '@maternity/mun-forms';
import {
  EmailField,
  patternValidator,
  SelectField,
  Submit,
  TextField,
} from '@maternity/mun-forms/vertical';
import { usStates } from '@maternity/mun-geography';
import { Button, Col, Row } from '@maternity/mun-layout';
import { Spinner } from '@maternity/mun-spinner';
import classNames from 'classnames';
import _ from 'lodash';
import createClient from 'openapi-fetch';
import * as React from 'react';

import peopleImgUrl from '../../images/signup/people.jpg';
import { BraintreeClient, GetNonce } from '../braintree/BraintreeClient';
import { CreditCardLogo } from '../braintree/CreditCardLogo';
import { HostedVerticalField } from '../braintree/HostedField';
import { Footer } from '../chrome';
import type { paths } from '../schemas/signup';
import { ContactInfo } from './ContactInfo';
import {
  DocuSignEvent,
  EmbeddedDocuSign,
  SessionEndType,
  SignRequestResponse,
} from './EmbeddedDocuSign';
import { ErrorInfo } from './ErrorInfo';
import { Ruler } from './Ruler';
import { ThankYou } from './ThankYou';

const client = createClient<paths>({
  baseUrl: `${env.STERLING_SERVICE_LOCATION}/signup/`,
});

const cardErrorMsg =
  'There was a problem processing your credit card; please double check your payment information and try again.';

const usStatesFullName = usStates.map((state) => ({
  label: state.label,
  value: state.label,
}));

type SignUpRequest = NonNullable<
  paths['/signup']['post']['requestBody']
>['content']['application/json'];
type FieldValues = SignUpRequest & {
  number: string;
  expirationDate: string;
  cvv: string;
};

async function fetchSignRequest(
  first_name: string,
  last_name: string,
  email: string,
  practice_name: string,
) {
  const { data, error } = await client.POST('/sign_request', {
    body: {
      name: first_name + ' ' + last_name,
      email,
      practice_name,
    },
  });
  if (error) {
    throw new Error(error[0].msg);
  }
  return data;
}

async function fetchClientToken() {
  const { data, error } = await client.GET('/braintree_client_token');
  if (error) {
    return 'invalid-token'; // let BraintreeClient handle the invalid token
  }
  return data!.client_token;
}

async function postSignUp(
  client_user_id: string,
  nonce: string,
  values: FieldValues,
) {
  const body = _.omit(values, ['number', 'expirationDate', 'cvv']);
  // remove empty strings from body:
  _.forIn(body, (value, key) => {
    if (value === '') {
      delete body[key as keyof typeof body];
    }
  });
  // add external fields to body:
  body.docusign_msa_client_user_id = client_user_id;
  body.payment_method_nonce = nonce;
  return client.POST('/signup', { body });
}

const SignUpInner = ({
  setIsDone,
}: {
  setIsDone: (isDone: boolean) => void;
}) => {
  const form = useForm<FieldValues>();
  const [step, setStep] = React.useState<1 | 2 | 3>(1);
  const [hasDocuSignError, setHasDocuSignError] =
    React.useState<boolean>(false);
  const [docusignSessionEndType, setDocusignSessionEndType] =
    React.useState<SessionEndType>();
  const [signRequestResponse, setSignRequestResponse] =
    React.useState<SignRequestResponse>();
  const [braintreeToken, setBraintreeToken] = React.useState<
    string | undefined
  >();
  const [cardType, setCardType] = React.useState<string | undefined>();
  const [braintreeErrorMsg, setBraintreeErrorMsg] = React.useState<
    string | undefined
  >();
  const step3Ref = React.useRef<HTMLHeadingElement>(null);
  const getNonceRef = React.useRef<GetNonce>((() => {}) as GetNonce);
  const postCodeValidators = React.useMemo(
    () => [patternValidator(/^\d{5}(-\d{4})?$/)],
    [],
  );
  const isMounted = useIsMounted();

  const backToStep1 = () => {
    // When user explicitly goes back to step 1, reset all DocuSign state
    setStep(1);
    setHasDocuSignError(false);
    setDocusignSessionEndType(undefined);
    setSignRequestResponse(undefined);
  };

  const goToStep2 = async () => {
    const formValues = form.getFormState().values;
    const { first_name, last_name, email, practice_name } = formValues;

    // Force validation w/o submit:
    form.setFieldTouched('first_name');
    form.setFieldTouched('last_name');
    form.setFieldTouched('email');
    form.setFieldTouched('practice_name');
    if (!first_name || !last_name || !email || !practice_name) {
      return;
    }

    setStep(2);
    setHasDocuSignError(false);
    try {
      const signRequest = await fetchSignRequest(
        first_name,
        last_name,
        email,
        practice_name,
      );
      setSignRequestResponse(signRequest);
    } catch (e) {
      setStep(1);
      setHasDocuSignError(true);
    }
  };

  const goToStep3 = (event: DocuSignEvent) => {
    if (event.sessionEndType !== 'signing_complete') {
      setDocusignSessionEndType(event.sessionEndType);
      setStep(1);
      return;
    }
    setStep(3);
  };

  const onSubmit = async (values: FieldValues) => {
    setBraintreeErrorMsg(undefined);

    const nonce = await getNonceRef.current();
    if (!nonce) {
      setBraintreeErrorMsg('Unknown error. Please try again.');
      return;
    }

    const { error } = await postSignUp(
      signRequestResponse!.client_user_id!,
      nonce,
      values,
    );
    if (error) {
      if (
        _.includes(error[0].loc, 'processor_declined') ||
        _.includes(error[0].loc, 'gateway_rejected')
      ) {
        setBraintreeErrorMsg(cardErrorMsg);
      } else {
        setBraintreeErrorMsg('Unknown error. Please try again.');
      }
      step3Ref.current?.scrollIntoView();
      return;
    }
    setIsDone(true);
  };

  React.useEffect(() => {
    fetchClientToken().then((client_token) => {
      if (isMounted()) {
        setBraintreeToken(client_token);
      }
    });
  }, [isMounted]);

  return (
    <>
      <Row className="get-started">
        <Col sm={6} lg={4} lgOffset={2}>
          <h1>Get Started</h1>
          <p>
            Signing up is easy and takes about five minutes. Simply enter your
            contact information, sign the user agreement, and enter your credit
            card information. We will email you instructions for logging in.
          </p>
          <p>
            Once you are logged into MN, you can add additional seats in
            practice settings.
          </p>
        </Col>
        <Col sm={6} lg={4}>
          <img className="signup-people-img hidden-xs" src={peopleImgUrl} />
        </Col>
      </Row>
      <Form form={form} onSubmit={onSubmit} blockUnloadWhenDirty={false}>
        <Row>
          <Col sm={12} lg={10} lgOffset={2}>
            <h2>
              Step 1: Enter Your Contact Information{' '}
              {step !== 1 && (
                <Button variant="default" onClick={backToStep1}>
                  <i className="fa fa-edit" />
                </Button>
              )}
            </h2>
          </Col>
        </Row>
        <Row>
          <Col sm={8} lg={4} lgOffset={2}>
            {step === 1 ? (
              <>
                {docusignSessionEndType && <ContactInfo />}
                {!docusignSessionEndType && hasDocuSignError && (
                  <ErrorInfo msg="Signature error. Please try again." />
                )}
                <Row>
                  <Col xs={6}>
                    <TextField
                      label="First Name"
                      form={form}
                      model="first_name"
                      required={true}
                    />
                  </Col>
                  <Col xs={6}>
                    <TextField
                      label="Last Name"
                      form={form}
                      model="last_name"
                      required={true}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <EmailField
                      label="Email"
                      form={form}
                      model="email"
                      required={true}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <TextField
                      label="Practice Name"
                      form={form}
                      model="practice_name"
                      required={true}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Button size="lg" variant="default" onClick={goToStep2}>
                      Next
                    </Button>
                  </Col>
                </Row>
              </>
            ) : null}
          </Col>
        </Row>
        <Ruler />
        <Row>
          <Col sm={12} lg={10} lgOffset={2}>
            <h2>Step 2: Complete the User Agreement</h2>
          </Col>
        </Row>
        <Row>
          <Col sm={12} lg={4} lgOffset={2}>
            {step === 2 ? (
              <EmbeddedDocuSign
                signRequestResponse={signRequestResponse}
                onSessionEnd={goToStep3}
              />
            ) : step === 3 ? (
              <p>
                A copy of the completed agreement has been sent to:{' '}
                {form.getFormState().values.email}
              </p>
            ) : null}
          </Col>
        </Row>
        <Ruler />
        <Row>
          <Col sm={12} lg={10} lgOffset={2}>
            <h2 ref={step3Ref}>Step 3: Enter Payment Information</h2>
          </Col>
        </Row>
        <Row>
          <Col sm={8} lg={4} lgOffset={2}>
            {step === 3 && braintreeToken ? (
              <BraintreeClient
                form={form}
                token={braintreeToken}
                onCardTypeChange={setCardType}
                getNonceRef={getNonceRef}
              >
                {braintreeErrorMsg && <ErrorInfo msg={braintreeErrorMsg} />}
                <TextField
                  label="Cardholder Name"
                  form={form}
                  model="cardholder_name"
                  required={true}
                  errorMessages={{ value_error: 'Invalid cardholder name' }}
                />
                <HostedVerticalField
                  form={form}
                  label="Card Number"
                  name="number"
                  className="input-glyph-group-right"
                >
                  {cardType && (
                    <CreditCardLogo
                      cardType={cardType}
                      className={classNames('input-glyph', 'margin-right-5')}
                    />
                  )}
                </HostedVerticalField>
                <Row>
                  <Col xs={8}>
                    <HostedVerticalField
                      form={form}
                      label="Expiration Date"
                      name="expirationDate"
                    />
                  </Col>
                  <Col xs={4}>
                    <HostedVerticalField form={form} label="CVV" name="cvv" />
                  </Col>
                </Row>
                <Row>
                  <Col xs={8}>
                    <TextField
                      label="Street Address"
                      form={form}
                      model="street_address"
                      required={true}
                      errorMessages={{ value_error: 'Invalid address' }}
                    />
                  </Col>
                  <Col xs={4}>
                    <TextField
                      label="Unit"
                      form={form}
                      model="extended_address"
                      errorMessages={{ value_error: 'Invalid address' }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={6}>
                    <TextField
                      label="City"
                      form={form}
                      model="locality"
                      required={true}
                      errorMessages={{ value_error: 'Invalid city' }}
                    />
                  </Col>
                  <Col xs={6}>
                    <SelectField
                      label="State"
                      form={form}
                      model="region"
                      options={usStatesFullName}
                      required={true}
                      errorMessages={{ value_error: 'Invalid state' }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={6}>
                    <TextField
                      label="Postal Code"
                      form={form}
                      model="postal_code"
                      required={true}
                      validators={postCodeValidators}
                      errorMessages={{
                        value_error: 'Invalid postal code',
                        pattern: 'Invalid postal code',
                      }}
                    />
                  </Col>
                </Row>
              </BraintreeClient>
            ) : (
              step === 3 && <Spinner />
            )}
          </Col>
        </Row>
        <Ruler />
        <Row>
          <Col sm={8} lg={4} lgOffset={2}>
            <Submit size="lg" disabled={step !== 3}>
              Sign me up!
            </Submit>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export const SignUp = () => {
  const [isDone, setIsDone] = React.useState<boolean>(false);

  return (
    <>
      <nav id="practice-nav" className="navbar navbar-default navbar-fixed-top">
        <span className="nav navbar-brand practice-name">
          Welcome to Maternity Neighborhood by Quilted Health!
        </span>
      </nav>
      <div id="middle" className="container-fluid signup-middle">
        {!isDone ? <SignUpInner setIsDone={setIsDone} /> : <ThankYou />}
      </div>
      <Footer />
    </>
  );
};
